import { useState, useRef, useEffect } from "react";
import { PlusCircle, X, ChevronDown } from "lucide-react";
import { useSetting } from "../../controllers/SettingsContext";

interface OperationsFormProps {
  type: "company" | "individual";
}

const DAYS = ["Mondays", "Tuesdays", "Wednesdays", "Thursdays", "Fridays", "Saturdays", "Sundays"];
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0") + ":00");
const PERIODS = ["AM", "PM"];

function Dropdown({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="flex flex-col relative w-full" ref={dropdownRef}>
      <label className="text-xs text-gray-500 mb-1">{label}</label>
      <div
        onClick={() => setOpen(!open)}
        className="border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white cursor-pointer flex justify-between items-center"
      >
        <span>{value || "Select"}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </div>

      {open && (
        <div className="absolute bottom-full mb-1 left-0 right-0 bg-white border border-gray-300 rounded-lg shadow-lg z-20 max-h-40 overflow-y-auto">
          {options.map((opt) => (
            <div
              key={opt}
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PAYMENT_TIMING_OPTIONS: {
  value: "before_delivery" | "after_delivery";
  title: string;
  description: string;
}[] = [
  {
    value: "before_delivery",
    title: "Before Delivery",
    description: "Customer pays when the order is placed or accepted, ahead of dispatch.",
  },
  {
    value: "after_delivery",
    title: "On/After Delivery",
    description: "Payment By transfer within the application is collected once the order is completed.",
  },
];

// Only relevant for "after delivery" — "before delivery" is wallet-only.
const PAYMENT_METHOD_OPTIONS: { value: "cash" | "wallet"; label: string }[] = [
  { value: "cash", label: "Cash" },
  { value: "wallet", label: "Wallet" },
];

export function OperationsForm({ type }: OperationsFormProps) {
  const {
    operationHours,
    setOperationHours,
    paymentTiming,
    setPaymentTiming,
    allowedPaymentMethods,
    setAllowedPaymentMethods,
  } = useSetting();
  const safeOperationHours = Array.isArray(operationHours) ? operationHours : [];

  // dynamic labels based on type
  const labels = {
    section: type === "company" ? "Operating hours" : "Availability hours",
    addButton: type === "company" ? "Add Business hour" : "Add Available hour",
    fromDay: "Available From",
    fromHour: "Hour",
    fromPeriod: "Period",
    toDay: "Available To",
    toHour: "Hour",
    toPeriod: "Period",
    remove: "Remove",
  };

  const addBusinessHour = () =>
    setOperationHours((prev: any) => [
      ...(Array.isArray(prev) ? prev : []),
      {
        id: Date.now() + Math.random(),
        fromDay: "",
        fromHour: "",
        fromPeriod: "AM",
        toDay: "",
        toHour: "",
        toPeriod: "PM",
      },
    ]);

  const updateBusinessHour = (id: number, field: string, value: string) =>
    setOperationHours((prev: any) =>
      (Array.isArray(prev) ? prev : []).map((hour) =>
        hour.id === id ? { ...hour, [field]: value } : hour
      )
    );

  const removeBusinessHour = (id: number) =>
    setOperationHours((prev: any) =>
      (Array.isArray(prev) ? prev : []).filter((hour) => hour.id !== id)
    );

  return (
    <div className="space-y-6">
      {type === "company" && (
        <div>
          <label className="block font-medium mb-2 border-l-4 border-blue-500 pl-2">
            Payment Collection
          </label>
          <p className="text-xs text-gray-500 mb-3">
            When should customers pay for an order?
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PAYMENT_TIMING_OPTIONS.map((option) => {
              const isSelected = paymentTiming === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPaymentTiming(option.value)}
                  className={`text-left border rounded-lg p-4 transition-colors ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-300 bg-white hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`w-4 h-4 rounded-full border flex items-center justify-center shrink-0 ${
                        isSelected ? "border-blue-600" : "border-gray-400"
                      }`}
                    >
                      {isSelected && <span className="w-2 h-2 rounded-full bg-blue-600" />}
                    </span>
                    <span className="font-medium text-sm">{option.title}</span>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">{option.description}</p>
                </button>
              );
            })}
          </div>

          {paymentTiming === "after_delivery" && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Which payment methods do you accept?
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Select at least one. This only applies to orders paid for after delivery.
              </p>
              <div className="flex flex-wrap gap-4">
                {PAYMENT_METHOD_OPTIONS.map((option) => {
                  const checked = allowedPaymentMethods.includes(option.value);
                  return (
                    <label key={option.value} className="flex items-center gap-2 text-sm cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setAllowedPaymentMethods((prev: string[]) =>
                            checked ? prev.filter((m) => m !== option.value) : [...prev, option.value]
                          )
                        }
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      {option.label}
                    </label>
                  );
                })}
              </div>
              {allowedPaymentMethods.length === 0 && (
                <p className="text-xs text-red-500 mt-2">Select at least one payment method.</p>
              )}
            </div>
          )}
        </div>
      )}

      <label className="block font-medium mb-2 border-l-4 border-blue-500 pl-2">
        {labels.section}
      </label>

      {safeOperationHours.map((hour) => (
        <div
          key={hour.id}
          className="relative bg-white border border-gray-200 rounded-lg p-4 shadow-sm mb-3"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-3">
            <Dropdown label={labels.fromDay} value={hour.fromDay} options={DAYS} onChange={(v) => updateBusinessHour(hour.id, "fromDay", v)} />
            <Dropdown label={labels.fromHour} value={hour.fromHour} options={HOURS} onChange={(v) => updateBusinessHour(hour.id, "fromHour", v)} />
            <Dropdown label={labels.fromPeriod} value={hour.fromPeriod} options={PERIODS} onChange={(v) => updateBusinessHour(hour.id, "fromPeriod", v)} />

            <Dropdown label={labels.toDay} value={hour.toDay} options={DAYS} onChange={(v) => updateBusinessHour(hour.id, "toDay", v)} />
            <Dropdown label={labels.toHour} value={hour.toHour} options={HOURS} onChange={(v) => updateBusinessHour(hour.id, "toHour", v)} />
            <Dropdown label={labels.toPeriod} value={hour.toPeriod} options={PERIODS} onChange={(v) => updateBusinessHour(hour.id, "toPeriod", v)} />
          </div>

          <button
            type="button"
            onClick={() => removeBusinessHour(hour.id)}
            className="text-red-500 hover:text-red-700 transition-colors flex items-center text-sm gap-1"
          >
            <X className="w-4 h-4" /> {labels.remove}
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={addBusinessHour}
        className="flex items-center text-blue-600 text-sm mt-2 hover:underline"
      >
        <PlusCircle className="w-4 h-4 mr-1" /> {labels.addButton}
      </button>
    </div>
  );
}
